import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Bot, Building2, ChartColumn, Plug, Users } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { formatTokens, type AdminUsageRow, type OrganisationDTO } from '@harness/shared';
import TopBar, { SearchButton } from '../../components/TopBar';
import { PageHeader, Seg, Spinner } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { usd } from '../../lib/format';
import { useSession } from '../../lib/queries';
import { toast } from '../../lib/store';
import IntegrationsAdminPage from './IntegrationsAdminPage';
import LibraryPage from './LibraryPage';
import PeoplePage, { PersonPage } from './PeoplePage';

// The admin section (organisation owners only): people and their provider keys, the agent library,
// spend per person and the organisation. Admins never see anyone's projects, only counts and spend.

const TABS = [
  { id: 'people', label: 'People', icon: Users },
  { id: 'agents', label: 'Agent library', icon: Bot },
  { id: 'integrations', label: 'Integrations', icon: Plug },
  { id: 'usage', label: 'Usage', icon: ChartColumn },
  { id: 'organisation', label: 'Organisation', icon: Building2 },
] as const;

export default function Admin() {
  const { tab = 'people', id } = useParams();
  const nav = useNavigate();
  const session = useSession();
  const user = session.data?.user;
  if (session.isLoading) return null;
  if (!user?.isAdmin) return <Navigate to="/" replace />;
  if (!TABS.some((t) => t.id === tab)) return <Navigate to="/admin/people" replace />;

  let content: ReactNode;
  if (tab === 'people') content = id ? <PersonPage userId={id} /> : <PeoplePage />;
  else if (tab === 'agents') content = <LibraryPage />;
  else if (tab === 'integrations') content = <IntegrationsAdminPage />;
  else if (tab === 'usage') content = <AdminUsagePage />;
  else content = <OrganisationPage />;

  return (
    <div className="screen">
      <TopBar
        settings={false}
        left={
          <button className="btn btn-secondary" onClick={() => nav('/')}>
            <ArrowLeft className="icon" />
            Back to projects
          </button>
        }
        right={<SearchButton />}
      />
      <div className="settings">
        <nav className="settings-nav card" aria-label="Admin">
          <div className="micro group">Admin · {user.organisation?.name ?? 'Organisation'}</div>
          {TABS.map((t) => (
            <Link key={t.id} className="nav-item" to={`/admin/${t.id}`} aria-current={tab === t.id ? 'page' : undefined}>
              <t.icon className="icon" />
              {t.label}
            </Link>
          ))}
        </nav>
        <main className="settings-main">{content}</main>
      </div>
    </div>
  );
}

function AdminUsagePage() {
  const [days, setDays] = useState('30');
  const q = useQuery({ queryKey: ['admin', 'usage', days], queryFn: () => api.get<AdminUsageRow[]>(`/api/admin/usage?days=${days}`) });
  const rows = q.data ?? [];
  const total = rows.reduce((a, r) => a + r.costUsd, 0);
  const max = Math.max(...rows.map((r) => r.costUsd), 0);
  return (
    <div className="settings-content">
      <PageHeader title="Usage" sub={`Spend per person across their own projects · ${usd(total)} in the last ${days} days. Project names aren't shown: projects are private.`}>
        <Seg
          value={days}
          onChange={setDays}
          options={[
            { value: '7', label: '7 days' },
            { value: '30', label: '30 days' },
            { value: '90', label: '90 days' },
          ]}
        />
      </PageHeader>
      {q.isLoading ? (
        <Spinner />
      ) : (
        <section className="card audit-card">
          <table className="table">
            <thead>
              <tr>
                <th>Person</th>
                <th style={{ width: 80 }}>Projects</th>
                <th style={{ width: 80 }}>Turns</th>
                <th style={{ width: 100 }}>Tokens in</th>
                <th style={{ width: 100 }}>Cached in</th>
                <th style={{ width: 100 }}>Tokens out</th>
                <th style={{ width: 100 }}>Est. cost</th>
                <th style={{ width: '18%' }} aria-label="Share" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.userId}>
                  <td>
                    <Link to={`/admin/people/${r.userId}`}>
                      <b>{r.name}</b>
                    </Link>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {r.email}
                    </div>
                  </td>
                  <td>{r.projects}</td>
                  <td>{r.turns}</td>
                  <td>{formatTokens(r.tokensIn)}</td>
                  <td className={r.cachedTokensIn ? undefined : 'faint'}>{r.cachedTokensIn ? formatTokens(r.cachedTokensIn) : '—'}</td>
                  <td>{formatTokens(r.tokensOut)}</td>
                  <td title={r.unpricedTokens ? `${formatTokens(r.unpricedTokens)} tokens from models with no price set` : undefined}>
                    {usd(r.costUsd)}
                    {r.unpricedTokens > 0 && <span className="faint"> +?</span>}
                  </td>
                  <td>
                    <div className="bar">
                      <span style={{ width: `${max > 0 ? Math.max(2, (r.costUsd / max) * 100) : 2}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={8} className="muted">
                    No usage in this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function OrganisationPage() {
  const qc = useQueryClient();
  const org = useQuery({ queryKey: ['admin', 'organisation'], queryFn: () => api.get<OrganisationDTO>('/api/admin/organisation') });
  const [name, setName] = useState('');
  useEffect(() => {
    if (org.data) setName(org.data.name);
  }, [org.data?.name]);
  if (!org.data) return <Spinner />;
  const save = async () => {
    try {
      qc.setQueryData(['admin', 'organisation'], await api.patch<OrganisationDTO>('/api/admin/organisation', { name: name.trim() }));
      void qc.invalidateQueries();
      toast('Organisation renamed');
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  return (
    <div className="settings-content narrow">
      <PageHeader title="Organisation" sub={`${org.data.members} people · ${org.data.admins} ${org.data.admins === 1 ? 'admin' : 'admins'}`} />
      <section className="card card-pad stack-sm">
        <div className="section-title">Name</div>
        <div className="row" style={{ flexWrap: 'nowrap', maxWidth: 520 }}>
          <input className="input" aria-label="Organisation name" value={name} onChange={(e) => setName(e.target.value)} />
          <button className="btn btn-secondary" disabled={!name.trim() || name.trim() === org.data.name} onClick={() => void save()}>
            Save
          </button>
        </div>
      </section>
      <section className="card card-pad stack-sm">
        <div className="section-title">How access works</div>
        <ul className="muted" style={{ fontSize: 14, lineHeight: 1.7, margin: 0, paddingLeft: 18 }}>
          <li>Everyone creates their own projects. A project is visible only to its creator and the people they invite. Admins can't see other people's projects.</li>
          <li>Each person's local projects live in their own workspace folder, named by their user id.</li>
          <li>Provider keys belong to a person. Admins can add or replace anyone's keys in People; people can manage their own too.</li>
          <li>The agent library is shared: everyone can add its agents to their projects.</li>
        </ul>
      </section>
    </div>
  );
}
